/* global chrome */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { t } from '@cyberint/common-utils';
import { CustomTooltip } from '../Poppers/CustomTooltip';
import Icons from '../Icons';
import { Themes } from '../../style/theme-consts';
import * as S from './styled';

/**
 * @param {string|node} title - The title to display - not mandatory
 * @param {string} valueToCopy - The value to copy to clipboard (required if `title` is not a string or if different from `title`)
 * @param {string} tooltip - The tooltip text to show on hover (default: 'Copy')
 * @param {string} copyTooltipText - The tooltip text to show after copying (default: 'Copied!')
 * @param {string} theme - The tooltip theme (default: `Themes.LIGHT`)
 * */
const CopyableTitle = ({
  title,
  tooltip = t('common.copy'),
  copyTooltipText = t('common.copied'),
  valueToCopy,
  theme = Themes.LIGHT
}) => {
  const [isCopied, setIsCopied] = useState(false);

  const copyToClipboardViaBackground = text => {
    try {
      chrome.runtime.sendMessage({ type: 'copy', text }, response => {
        if (response?.success) {
          setIsCopied(true);
          setTimeout(() => setIsCopied(false), 2500);
        } else {
          setIsCopied(false);
        }
      });
    } catch (err) {
      setIsCopied(false);
    }
  };

  const copyToClipBoard = e => {
    e.preventDefault();
    e.stopPropagation();

    const text = valueToCopy || (typeof title === 'string' ? title : title?.props?.children || '');

    // Check if we're in a Chrome extension context first
    if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage) {
      // Try background script method first for our Threat Intelligence Chrome extensions
      copyToClipboardViaBackground(text);
    } else if (navigator.clipboard && navigator.clipboard.writeText) {
      // For regular web contexts
      navigator.clipboard
        .writeText(text)
        .then(() => {
          setIsCopied(true);
          // Auto reset after tooltip disappears
          setTimeout(() => setIsCopied(false), 2500);
        })
        .catch(() => {
          setIsCopied(false);
        });
    } else {
      setIsCopied(false);
    }
  };

  return (
    <S.Wrapper>
      {title && (typeof title === 'string' ? <S.Title>{title}</S.Title> : title)}
      <CustomTooltip mode={theme} title={isCopied ? copyTooltipText : tooltip} arrow placement="top" leaveDelay={300}>
        <S.CopyIcon>{isCopied ? <Icons.DoneAll /> : <Icons.Copy onClick={copyToClipBoard} />}</S.CopyIcon>
      </CustomTooltip>
    </S.Wrapper>
  );
};

CopyableTitle.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.element]),
  tooltip: PropTypes.string,
  theme: PropTypes.oneOf(['dark-mode', 'light-mode']),
  valueToCopy: PropTypes.string,
  copyTooltipText: PropTypes.string
};

export default CopyableTitle;
